import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { useFirebase } from '../App';

const LoginPage = ({ navigate }) => {
    // Access global firebase instances
    const { auth, isAuthReady, saveUserThemePreference } = useFirebase();
    
    // State
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // --- Handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!isAuthReady) {
            setMessage('System is initializing. Please wait a moment...');
            return;
        }

        setLoading(true);
        setMessage('');
        setIsError(false);

        try {
            await signInWithEmailAndPassword(auth, formData.email, formData.password);
            
            // Success Logic
            setMessage('Login successful! Redirecting...');
            saveUserThemePreference(true); // Enforce Dark Theme on login
            
            // Redirect after 1 second
            setTimeout(() => {
                navigate('home');
            }, 1000);

        } catch (error) {
            console.error("Login Error:", error);
            setIsError(true);
            
            // User-friendly error messages
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                setMessage("Incorrect email or password.");
            } else if (error.code === 'auth/too-many-requests') {
                setMessage("Access temporarily disabled due to too many failed attempts. Reset your password or try again later.");
            } else {
                setMessage("Login failed. Please try again.");
            }
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!formData.email) {
            setIsError(true);
            setMessage('Please enter your email address first.');
            return;
        }

        setLoading(true);
        setMessage('');
        setIsError(false);

        try {
            await sendPasswordResetEmail(auth, formData.email);
            setIsError(false);
            setMessage('Password reset email sent! Please check your inbox.');
        } catch (error) {
            setIsError(true);
            if (error.code === 'auth/user-not-found') {
                setMessage("No account found with this email.");
            } else {
                setMessage("Failed to send reset email: " + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="container mx-auto px-6 py-16 animate-fade-in" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-body-text)' }}>
            <h2 className="text-4xl font-bold text-center mb-8">Delegate Login</h2>
            
            <div className="p-8 rounded-lg shadow-xl max-w-md mx-auto border" 
                 style={{ backgroundColor: 'var(--color-section1-card-bg)', borderColor: 'var(--color-section1-card-border)' }}>
                
                <form onSubmit={handleLogin} className="space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                        <input 
                            type="email" 
                            name="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 text-black" 
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium mb-1">Password</label>
                        <input 
                            type="password" 
                            name="password" 
                            value={formData.password} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 text-black" 
                        />
                    </div>

                    <button 
                        type="submit" 
                        disabled={loading}
                        className={`w-full py-3 px-4 rounded-md font-bold text-white transition-colors shadow-lg
                        ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {loading ? 'Logging In...' : 'Log In'}
                    </button>
                </form>

                {/* Additional Actions */}
                <div className="mt-4 text-center space-y-3">
                    <button 
                        onClick={handleForgotPassword} 
                        disabled={loading}
                        className="text-blue-500 hover:underline text-sm font-medium block w-full"
                    >
                        Forgot your password?
                    </button>
                    
                    <div className="text-sm pt-4 border-t border-gray-200" style={{ borderColor: 'var(--color-section1-card-border)' }}>
                        Don't have an account?{' '}
                        <button 
                            onClick={() => navigate('register')} 
                            className="text-blue-500 hover:underline font-bold"
                        >
                            Register Here
                        </button>
                    </div>
                </div>

                {/* Status Message */}
                {message && (
                    <div className={`mt-6 p-3 rounded-md text-center text-sm font-medium
                        ${isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {message}
                    </div>
                )}
            </div>
        </section>
    );
};

export default LoginPage;